import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import ActivityLogs from '../components/ActivityLogs';
import Reports from '../components/Reports';

/*
  KraKpiTemplates.jsx
  - Ultimate combined KRA/KPI + OKR + Cascading + Workflow builder page
  - Persists templates to localStorage (key: kra_kpi_templates_v2)
  - Font: Poppins + Roboto (imported inline)
  - Drag & Drop: HTML5 drag events for simplicity & zero extra deps
  - Note: This is a client-only demo engine. Replace localStorage with API calls if needed.
*/

/* ---------------------------
  Utility helpers
  --------------------------- */
const LS_KEY = "kra_kpi_templates_v2_v1";

const uid = (prefix = "id") => `${prefix}_${Date.now().toString(36)}_${Math.floor(Math.random() * 1000)}`;

const defaultTemplate = (overrides = {}) => ({
  id: uid("tpl"),
  name: overrides.name || "New Performance Template",
  type: overrides.type || "KRA", // 'KRA' or 'OKR' or 'Hybrid'
  description: overrides.description || "",
  krAs: overrides.krAs || [], // array of KRA objects
  okrs: overrides.okrs || [], // array of { objective, keyResults[] }
  workflow: overrides.workflow || [{ id: uid("wf"), name: "Manager Approval", role: "Manager", active: true }],
  cascading: overrides.cascading || { enabled: true }, // allow cascading
  versions: overrides.versions || [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

/* Evaluate simple formula safely-ish:
  Allowed tokens: numbers, + - * / ( ) spaces, references like KPI:value (we support simple named tokens)
  For demo: use Function but restrict characters.
*/
function safeEvalFormula(expr = "", context = {}) {
  try {
    if (!expr || typeof expr !== "string") return 0;
    // allow digits, operators, whitespace, dots, letters, parentheses, commas
    if (!/^[0-9+\-*/().,%\sA-Za-z_]+$/.test(expr)) return NaN;
    // replace tokens in {{TOKEN}} or TOKEN with context values
    let safeExpr = expr.replace(/\{\{(\w+)\}\}/g, (m, p1) => {
      const val = context[p1];
      return typeof val === "number" ? val : Number(context[p1] || 0);
    });
    // also replace any bare tokens like VAR1 with context[VAR1]
    safeExpr = safeExpr.replace(/\b([A-Za-z_]\w*)\b/g, (m, p1) => {
      if (Object.prototype.hasOwnProperty.call(context, p1)) {
        return Number(context[p1]) || 0;
      }
      // keep numeric words untouched
      if (!isNaN(Number(m))) return m;
      return 0;
    });
    // eslint-disable-next-line no-new-func
    const fn = new Function(`return (${safeExpr});`);
    return Number(fn());
  } catch (e) {
    return NaN;
  }
}

/* ---------------------------
   FIXED / OPTIMIZED MODAL
--------------------------- */
const Modal = ({ isOpen, onClose, title, children, width = "max-w-4xl" }) => {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex justify-center bg-black/40 backdrop-blur-sm"
        style={{
          alignItems: "flex-start",
          paddingTop: "100px",       // ← avoids header overlap
          overflow: "hidden"
        }}
      >
        <motion.div
          initial={{ scale: 0.98, y: 15 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.98, y: 15 }}
          className={`bg-white rounded-2xl shadow-2xl w-full ${width}
            max-h-[75vh]               // ← shorter modal
            flex flex-col overflow-hidden
          `}
        >
          {/* Header (Sticky) */}
          <div className="p-4 border-b bg-white sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{title}</h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-800 px-2 py-1 rounded"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Modal Scrollable Body */}
          <div className="p-6 overflow-y-auto flex-1">
            {children}
          </div>

          {/* Footer (Sticky) */}
          <div className="p-4 border-t bg-white sticky bottom-0 z-10 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 text-sm"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};


/* ---------------------------
  Component: KPI Editor Row
  --------------------------- */
const KPIEditorRow = ({ kpi, onChange, onDelete }) => {
  // kpi: { id, name, type, weight, target, ratingScale, formula }
  const update = (patch) => onChange({ ...kpi, ...patch });
  const sampleContext = useMemo(() => ({ SALES: 1000, TARGET: 1200, CONVERSION: 5 }), []);
  
  return (
    <div className="border rounded-lg p-3 bg-white flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 space-y-1">
          <input
            value={kpi.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder={`KPI name`}
            className="w-full px-3 py-2 border rounded-md font-medium"
          />
          <textarea
            value={kpi.description}
            onChange={(e) => update({ description: e.target.value })}
            placeholder={`Description/metric detail...`}
            className="w-full px-3 py-1 border rounded-md text-sm text-gray-700 h-16 resize-none"
          />
        </div>
        <div className="w-36">
          <label className="block text-xs text-gray-600">Weight (%)</label>
          <input
            type="number"
            value={kpi.weight}
            onChange={(e) => update({ weight: Number(e.target.value || 0) })}
            className="w-full px-2 py-1 border rounded-md"
            min={0}
            max={100}
          />
        </div>
        <div className="w-56">
          <label className="block text-xs text-gray-600">Type</label>
          <select value={kpi.type} onChange={(e) => update({ type: e.target.value })} className="w-full px-2 py-1 border rounded-md">
            <option value="numeric">Numeric (target)</option>
            <option value="rating">Rating (1..N)</option>
            <option value="boolean">Boolean (Achieved/Not)</option>
            <option value="milestone">Milestone</option>
            <option value="formula">Formula</option>
          </select>
        </div>
      </div>

      {/* conditional fields */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-gray-600">Target / Details</label>
          <input
            value={kpi.target || ""}
            onChange={(e) => update({ target: e.target.value })}
            className="w-full px-3 py-2 border rounded-md"
            placeholder="e.g., 95 or 1-5 or milestone text"
          />
        </div>
        <div>
          <label className="text-xs text-gray-600">Rating Scale (for rating)</label>
          <input
            disabled={kpi.type !== "rating"}
            value={kpi.ratingScale || "5"}
            onChange={(e) => update({ ratingScale: e.target.value })}
            className="w-full px-3 py-2 border rounded-md disabled:bg-gray-100"
            placeholder="e.g., 5"
          />
        </div>
        <div>
          <label className="text-xs text-gray-600">Formula (optional)</label>
          <input
            disabled={kpi.type !== "formula"}
            value={kpi.formula || ""}
            onChange={(e) => update({ formula: e.target.value })}
            className="w-full px-3 py-2 border rounded-md font-mono text-sm disabled:bg-gray-100"
            placeholder='e.g., ({{SALES}} / {{TARGET}}) * 100'
          />
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t mt-2">
        <div className="text-xs text-gray-500">
          Preview ({kpi.type === 'formula' ? 'Context: SALES=1000' : '—'}): <span className="font-semibold text-slate-800">{safeEvalFormula(kpi.formula || kpi.target || "", sampleContext) || "—"}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => onDelete(kpi.id)} className="text-rose-600 px-2 py-1 rounded hover:bg-rose-50 text-sm">Delete KPI</button>
        </div>
      </div>
    </div>
  );
};


/* ---------------------------
  Main Page Component
  --------------------------- */
export default function KraKpiTemplates() {
  // fonts inline
  const fontImport = `@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&family=Roboto:wght@300;400;500;700&display=swap');`;
  const fontStyle = { fontFamily: "Poppins, Roboto, sans-serif" };

  const [templates, setTemplates] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return [defaultTemplate({ name: "Default KRA Template", type: "KRA" })];
      return JSON.parse(raw);
    } catch {
      return [defaultTemplate({ name: "Default KRA Template", type: "KRA" })];
    }
  });

  const [selectedTemplateId, setSelectedTemplateId] = useState(templates[0]?.id || null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorTemplate, setEditorTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('templates');
  
  // KRA KPI Templates specific activity types
  const activityTypes = [
    "Template Created", "Template Updated", "Template Deleted", 
    "KRA Added", "KPI Modified", "Workflow Updated"
  ];

  // KRA KPI Templates specific report types
  const reportTypes = [
    "KRA KPI Templates Report", "Template Usage Report", 
    "Performance Metrics Report", "Workflow Analysis Report",
    "Template Effectiveness Report", "KPI Performance Report"
  ];

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(templates));
  }, [templates]);

  useEffect(() => {
    if (!selectedTemplateId && templates.length > 0) setSelectedTemplateId(templates[0].id);
  }, [templates, selectedTemplateId]);

  const selectedTemplate = useMemo(() => templates.find((t) => t.id === selectedTemplateId) || null, [templates, selectedTemplateId]);

  /* Template CRUD */
  const createTemplate = (type = "KRA") => {
    const tpl = defaultTemplate({ name: `${type} Template ${templates.length + 1}`, type });
    setTemplates((p) => [tpl, ...p]);
    setSelectedTemplateId(tpl.id);
    setEditorTemplate(tpl);
    setIsEditorOpen(true);
  };

  const saveTemplate = (tpl) => {
    setTemplates((prev) => {
      const idx = prev.findIndex((x) => x.id === tpl.id);
      const updated = { ...tpl, updatedAt: new Date().toISOString(), versions: [...(tpl.versions || []), { snapshotAt: new Date().toISOString(), snapshot: tpl }] };
      if (idx === -1) return [updated, ...prev];
      const cp = [...prev];
      cp[idx] = updated;
      return cp;
    });
    setIsEditorOpen(false);
    setEditorTemplate(null);
  };

  const deleteTemplate = (id) => {
    if (!window.confirm("Delete template and cascades? This is irreversible.")) return;
    setTemplates((p) => p.filter((t) => t.id !== id));
    if (selectedTemplateId === id) setSelectedTemplateId(null);
  };

  /* KRA & KPI operations */
  const addKRA = (tplId) => {
    const newKra = {
      id: uid("kra"),
      title: "New KRA: Financial Growth",
      description: "Key result area focused on revenue and profitability targets.",
      weight: 100,
      kpis: [], // each kpi: { id, name, type, weight, target, formula }
      childrenTemplates: [], // cascading children template IDs
    };
    setTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, krAs: [...t.krAs, newKra], updatedAt: new Date().toISOString() } : t)));
  };

  const updateKRA = (tplId, kraId, patch) => {
    setTemplates((prev) =>
      prev.map((t) => {
        if (t.id !== tplId) return t;
        return {
          ...t,
          krAs: t.krAs.map((k) => (k.id === kraId ? { ...k, ...patch } : k)),
          updatedAt: new Date().toISOString(),
        };
      })
    );
  };

  const removeKRA = (tplId, kraId) => {
    if (!window.confirm("Remove KRA?")) return;
    setTemplates((prev) =>
      prev.map((t) => (t.id === tplId ? { ...t, krAs: t.krAs.filter((k) => k.id !== kraId), updatedAt: new Date().toISOString() } : t))
    );
  };

  const addKPI = (tplId, kraId) => {
    const newKpi = { id: uid("kpi"), name: "New KPI: Revenue Target", type: "numeric", weight: 50, target: "500K USD", ratingScale: "5", formula: "", preview: null, description: "" };
    setTemplates((prev) =>
      prev.map((t) => {
        if (t.id !== tplId) return t;
        return {
          ...t,
          krAs: t.krAs.map((k) => (k.id === kraId ? { ...k, kpis: [...k.kpis, newKpi] } : k)),
          updatedAt: new Date().toISOString(),
        };
      })
    );
  };

  const updateKPI = (tplId, kraId, kpiId, patch) => {
    setTemplates((prev) =>
      prev.map((t) => {
        if (t.id !== tplId) return t;
        return {
          ...t,
          krAs: t.krAs.map((k) =>
            k.id === kraId
              ? { ...k, kpis: k.kpis.map((kp) => (kp.id === kpiId ? { ...kp, ...patch } : kp)) }
              : k
          ),
          updatedAt: new Date().toISOString(),
        };
      })
    );
  };

  const removeKPI = (tplId, kraId, kpiId) => {
    if (!window.confirm("Remove KPI?")) return;
    setTemplates((prev) =>
      prev.map((t) => {
        if (t.id !== tplId) return t;
        return { ...t, krAs: t.krAs.map((k) => (k.id === kraId ? { ...k, kpis: k.kpis.filter((p) => p.id !== kpiId) } : k)), updatedAt: new Date().toISOString() };
      })
    );
  };

  /* Re-ordering via HTML5 DnD for KRAs and KPIs */
  const dragState = useRef({ sourceType: null, sourceTpl: null, sourceId: null });

  const onDragStart = (e, payload) => {
    dragState.current = payload; // { sourceType: 'kra'|'kpi', sourceTpl, sourceKraId?, sourceKpiId? }
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", JSON.stringify(payload)); } catch (error) { /* ignored */ }
  };

  const onDropOnKraList = (e, tplId, destIndex) => {
    e.preventDefault();
    const payload = dragState.current;
    if (!payload || !payload.sourceType) return;
    
    setTemplates((prev) => {
      let cp = JSON.parse(JSON.stringify(prev));
      const srcTpl = cp.find((x) => x.id === payload.sourceTpl);
      const destTpl = cp.find((x) => x.id === tplId);
      if (!srcTpl || !destTpl) return prev;

      if (payload.sourceType === "kra") {
        const kra = srcTpl.krAs.find((k) => k.id === payload.sourceId);
        if (!kra) return prev;
        // remove from source
        srcTpl.krAs = srcTpl.krAs.filter((k) => k.id !== payload.sourceId);
        // insert into dest at destIndex (if destIndex === -1 push end)
        if (destIndex === -1 || destIndex >= destTpl.krAs.length) destTpl.krAs.push(kra);
        else destTpl.krAs.splice(destIndex, 0, kra);
      }
      // Note: KPI drag and drop between KRAs is complex and better handled in onDropOnKpiList for specific KRA target

      return cp;
    });
    dragState.current = {};
  };

  const onDropOnKpiList = (e, tplId, kraId, destIndex) => {
    e.preventDefault();
    const payload = dragState.current;
    if (!payload || payload.sourceType !== "kpi") return;

    setTemplates((prev) => {
      const cp = JSON.parse(JSON.stringify(prev));
      const srcTpl = cp.find((x) => x.id === payload.sourceTpl);
      const destTpl = cp.find((x) => x.id === tplId);
      if (!srcTpl || !destTpl) return prev;
      const srcKra = srcTpl.krAs.find((k) => k.id === payload.sourceKraId);
      const destKra = destTpl.krAs.find((k) => k.id === kraId);
      if (!srcKra || !destKra) return prev;
      const kpi = srcKra.kpis.find((kp) => kp.id === payload.sourceKpiId);
      if (!kpi) return prev;
      
      // Remove from source KRA's KPI list
      srcKra.kpis = srcKra.kpis.filter((kp) => kp.id !== payload.sourceKpiId);
      
      // Insert into destination KRA's KPI list
      if (destIndex === -1 || destIndex >= destKra.kpis.length) destKra.kpis.push(kpi);
      else destKra.kpis.splice(destIndex, 0, kpi);
      
      return cp;
    });
    dragState.current = {};
  };

  /* OKRs operations */
  const addObjective = (tplId) => {
    const obj = { id: uid("obj"), title: "New Objective: Drive Customer Success", description: "", keyResults: [] };
    setTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, okrs: [...t.okrs, obj], updatedAt: new Date().toISOString() } : t)));
  };

  const updateObjective = (tplId, objId, patch) => {
    setTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, okrs: t.okrs.map((o) => (o.id === objId ? { ...o, ...patch } : o)), updatedAt: new Date().toISOString() } : t)));
  };

  const removeObjective = (tplId, objId) => {
    if (!window.confirm("Remove Objective?")) return;
    setTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, okrs: t.okrs.filter((o) => o.id !== objId), updatedAt: new Date().toISOString() } : t)));
  };

  const addKeyResult = (tplId, objId) => {
    const kr = { id: uid("kr"), name: "New Key Result: Increase Net Promoter Score", target: "NPS of 50", score: 0, formula: "NPS value" };
    setTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, okrs: t.okrs.map((o) => (o.id === objId ? { ...o, keyResults: [...o.keyResults, kr] } : o)), updatedAt: new Date().toISOString() } : t)));
  };

  const updateKeyResult = (tplId, objId, krId, patch) => {
    setTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, okrs: t.okrs.map((o) => (o.id === objId ? { ...o, keyResults: o.keyResults.map((kr) => (kr.id === krId ? { ...kr, ...patch } : kr)) } : o)), updatedAt: new Date().toISOString() } : t)));
  };

  const removeKeyResult = (tplId, objId, krId) => {
    if (!window.confirm("Remove Key Result?")) return;
    setTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, okrs: t.okrs.map((o) => (o.id === objId ? { ...o, keyResults: o.keyResults.filter((kr) => kr.id !== krId) } : o)), updatedAt: new Date().toISOString() } : t)));
  };

  /* Workflow */
  const addWorkflowStep = (tplId) => {
    const step = { id: uid("wf"), name: "New Step", role: "Manager", active: true };
    setTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, workflow: [...t.workflow, step], updatedAt: new Date().toISOString() } : t)));
  };

  const updateWorkflowStep = (tplId, stepId, patch) => {
    setTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, workflow: t.workflow.map((s) => (s.id === stepId ? { ...s, ...patch } : s)), updatedAt: new Date().toISOString() } : t)));
  };

  const removeWorkflowStep = (tplId, stepId) => {
    if (!window.confirm("Remove workflow step?")) return;
    setTemplates((prev) => prev.map((t) => (t.id === tplId ? { ...t, workflow: t.workflow.filter((s) => s.id !== stepId), updatedAt: new Date().toISOString() } : t)));
  };

  /* cascading: attach child template id to parent.krAs[].childrenTemplates */
  const attachChildTemplateToKRA = (parentTplId, kraId, childTplId) => {
    setTemplates((prev) => prev.map((t) => {
      if (t.id !== parentTplId) return t;
      return { ...t, krAs: t.krAs.map((k) => k.id === kraId ? { ...k, childrenTemplates: [...new Set([...(k.childrenTemplates || []), childTplId])] } : k), updatedAt: new Date().toISOString() };
    }));
  };

  const detachChildTemplateFromKRA = (parentTplId, kraId, childTplId) => {
    setTemplates((prev) => prev.map((t) => {
      if (t.id !== parentTplId) return t;
      return { ...t, krAs: t.krAs.map((k) => k.id === kraId ? { ...k, childrenTemplates: (k.childrenTemplates || []).filter(id => id !== childTplId) } : k), updatedAt: new Date().toISOString() };
    }));
  };

  /* validation helpers */
  const validateWeights = (tpl) => {
    const kraSum = tpl.krAs.reduce((s, k) => s + Number(k.weight || 0), 0);
    const kraWarnings = tpl.krAs.map((k) => {
      const kpSum = (k.kpis || []).reduce((s, kp) => s + Number(kp.weight || 0), 0);
      return { kraId: k.id, kpSum };
    });
    return { kraSum, kraWarnings };
  };

  /* Export / Import templates */
  const exportTemplate = (tplId) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    const blob = new Blob([JSON.stringify(tpl, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tpl.name.replace(/\s+/g, "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importTemplates = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        const importedTemplates = Array.isArray(parsed) ? parsed : [parsed];
        
        // Ensure unique IDs before adding
        const uniqueTemplates = importedTemplates.map(t => ({
          ...t,
          id: uid('tpl_imported'),
          name: t.name + ' (Imported)'
        }));
        
        setTemplates((p) => [...uniqueTemplates, ...p]);
        // Replace window.alert with a console message or custom UI feedback
        console.log("Import succeeded: Templates added.");
      } catch (err) {
        console.error("Invalid JSON during import.", err);
        // Replace window.alert with a console message or custom UI feedback
      }
    };
    reader.readAsText(file);
  };

  /* quick actions */
  const duplicateTemplate = (tplId) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    const copy = { ...JSON.parse(JSON.stringify(tpl)), id: uid("tpl"), name: `${tpl.name} (Copy)`, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    setTemplates((p) => [copy, ...p]);
  };

  /* UI handlers for editor modal */
  const openEditor = (tpl) => {
    setEditorTemplate(JSON.parse(JSON.stringify(tpl))); // deep clone
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorTemplate(null);
    setIsEditorOpen(false);
  };

  /* small helpers for drag zones */
  const allowDrop = (e) => e.preventDefault();

  /* render */
  return (
    <div className="min-h-screen bg-slate-50 p-6" style={fontStyle}>
      <style>{fontImport}</style>

      <div className="max-w-7xl mx-auto space-y-6">
        <header className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Performance Templates Engine</h1>
            <p className="text-sm text-slate-600 mt-1">Combined KRA/KPI · OKR · Cascading · Workflow · Scoring · Versioning</p>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => setActiveTab('activity')} 
              className={`px-4 py-2 rounded-md text-sm transition ${
                activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              📊 Activity Logs
            </button>
            
            <button 
              onClick={() => setActiveTab('reports')} 
              className={`px-4 py-2 rounded-md text-sm transition ${
                activeTab === 'reports' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              📈 Reports
            </button>
            
            <button 
              onClick={() => setActiveTab('templates')} 
              className={`px-4 py-2 rounded-md text-sm transition ${
                activeTab === 'templates' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              📋 Templates
            </button>
            
            <label className="text-sm text-slate-600">Import JSON</label>
            <input
              type="file"
              accept="application/json"
              onChange={(e) => e.target.files?.[0] && importTemplates(e.target.files[0])}
              className="text-sm border rounded p-1 bg-white"
            />
            <button onClick={() => createTemplate("KRA")} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition">New KRA Template</button>
            <button onClick={() => createTemplate("OKR")} className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition">New OKR Template</button>
            <button onClick={() => createTemplate("Hybrid")} className="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 transition">New Hybrid Template</button>
          </div>
        </header>

        {/* Activity Logs Tab */}
        {activeTab === 'activity' && (
          <ActivityLogs 
            moduleName="KRA KPI Templates" 
            activityTypes={activityTypes} 
          />
        )}
        
        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <Reports 
            moduleName="KRA KPI Templates" 
            reportTypes={reportTypes} 
          />
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
        <main className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar: Templates list */}
          <aside className="col-span-1 bg-white p-4 rounded-2xl shadow-sm h-[72vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-800">Templates</h3>
              <div className="text-xs text-slate-500">{templates.length}</div>
            </div>
            <div className="space-y-2">
              {templates.map((tpl) => (
                <div key={tpl.id} className={`p-3 rounded-lg cursor-pointer transition ${tpl.id === selectedTemplateId ? "ring-2 ring-indigo-500 bg-indigo-50" : "bg-white hover:bg-slate-100"}`}>
                  <div className="flex items-start justify-between">
                    <div onClick={() => setSelectedTemplateId(tpl.id)} className="flex-1">
                      <div className="text-sm font-semibold text-slate-900">{tpl.name}</div>
                      <div className="text-xs text-slate-500 mt-1">{tpl.type} · {tpl.krAs.length} KRA · {tpl.okrs.length} OKRs</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <button title="Edit" onClick={() => openEditor(tpl)} className="text-indigo-600 text-xs px-2 py-1 rounded hover:bg-indigo-100">Edit</button>
                      <div className="flex gap-1">
                        <button title="Duplicate" onClick={() => duplicateTemplate(tpl.id)} className="text-slate-600 text-xs px-1 hover:text-indigo-600">Dup</button>
                        <button title="Export" onClick={() => exportTemplate(tpl.id)} className="text-slate-600 text-xs px-1 hover:text-indigo-600">Exp</button>
                        <button title="Delete" onClick={() => deleteTemplate(tpl.id)} className="text-rose-600 text-xs px-1 hover:text-rose-800">Del</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </aside>

          {/* Main canvas */}
          <section className="col-span-3 bg-white p-6 rounded-2xl shadow-lg min-h-[72vh] overflow-y-auto">
            {!selectedTemplate ? (
              <div className="text-center text-slate-600 p-10">Select or create a template to start building KRAs or OKRs.</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900">{selectedTemplate.name}</h2>
                    <div className="text-sm text-slate-500">{selectedTemplate.type} · Updated: {new Date(selectedTemplate.updatedAt).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEditor(selectedTemplate)} className="px-3 py-1 border rounded-lg text-sm bg-slate-100 hover:bg-slate-200">Open Editor</button>
                    <button onClick={() => exportTemplate(selectedTemplate.id)} className="px-3 py-1 border rounded-lg text-sm bg-slate-100 hover:bg-slate-200">Export</button>
                  </div>
                </div>

                {/* Summary row: weights validation */}
                <div className="mb-6 p-4 rounded-xl border-t-4 border-indigo-500 bg-indigo-50 shadow-inner">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                      <div className="text-sm text-slate-600 font-medium">KRA Total Weight</div>
                      <div className={`text-xl font-bold ${validateWeights(selectedTemplate).kraSum === 100 ? 'text-indigo-700' : 'text-rose-600'}`}>
                        {validateWeights(selectedTemplate).kraSum}% {validateWeights(selectedTemplate).kraSum !== 100 && <span className="text-sm font-normal">(Should be 100%)</span>}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-slate-600 font-medium">Weighting Rule</div>
                      <div className="text-sm text-slate-700">KRAs should sum to 100%. Each KRA's KPIs should also sum to 100% *within* that KRA.</div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-600 font-medium">Workflow steps</div>
                      <div className="text-xl font-medium text-slate-900">{(selectedTemplate.workflow || []).length}</div>
                    </div>
                  </div>
                </div>

                {/* Builder grid: KRA/KPI and OKR/Workflow */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left: KRA / KPI builder (2/3 width) */}
                  <div className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-semibold text-slate-800">Key Result Areas (KRAs)</h3>
                      <button onClick={() => addKRA(selectedTemplate.id)} className="px-3 py-1 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition shadow-md">Add KRA</button>
                    </div>

                    <div
                      onDragOver={allowDrop}
                      onDrop={(e) => onDropOnKraList(e, selectedTemplate.id, -1)}
                      className="space-y-4 min-h-[100px] p-2 border-dashed border-2 border-slate-200 rounded-lg"
                    >
                      {selectedTemplate.krAs.length === 0 && <div className="text-sm text-slate-500 p-8 text-center">Drag KRAs here or click "Add KRA".</div>}
                      {selectedTemplate.krAs.map((kra, idx) => (
                        <div
                          key={kra.id}
                          draggable
                          onDragStart={(e) => onDragStart(e, { sourceType: "kra", sourceTpl: selectedTemplate.id, sourceId: kra.id })}
                          className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 pr-4">
                              <input
                                value={kra.title}
                                onChange={(e) => updateKRA(selectedTemplate.id, kra.id, { title: e.target.value })}
                                className="w-full px-2 py-1 border-b text-xl font-bold focus:outline-none focus:border-indigo-400 transition"
                                placeholder="KRA title"
                              />
                              <textarea
                                value={kra.description}
                                onChange={(e) => updateKRA(selectedTemplate.id, kra.id, { description: e.target.value })}
                                className="w-full px-2 py-1 text-sm text-slate-600 resize-none h-12 mt-1 border rounded-md"
                                placeholder="Description (e.g., Increase market share in APAC)"
                              />
                              <div className="mt-2 flex items-center gap-3">
                                <div className="w-28">
                                  <label className="text-xs text-slate-500">KRA Weight (%)</label>
                                  <input type="number" value={kra.weight || 0} onChange={(e) => updateKRA(selectedTemplate.id, kra.id, { weight: Number(e.target.value || 0) })} className="w-full px-2 py-1 border rounded-md text-center" />
                                </div>
                                <div className="text-xs text-slate-500">Cascaded Children: {(kra.childrenTemplates || []).length}</div>
                                <div className="ml-auto text-sm text-slate-400">Drag KRA to reorder</div>
                              </div>
                            </div>

                            <div className="ml-4 flex flex-col gap-2 pt-2">
                              <button onClick={() => addKPI(selectedTemplate.id, kra.id)} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-sm hover:bg-emerald-200 transition">+ Add KPI</button>
                              <button onClick={() => removeKRA(selectedTemplate.id, kra.id)} className="px-3 py-1 text-rose-600 rounded-lg hover:bg-rose-50 text-sm border">Delete KRA</button>
                            </div>
                          </div>

                          {/* KPI list */}
                          <div className="mt-4 space-y-3 p-3 border-t border-slate-100">
                            <div className="text-sm font-medium text-slate-700">Key Performance Indicators (KPIs)</div>
                            {(kra.kpis || []).length === 0 && <div className="text-xs text-slate-500 text-center py-4">No KPIs for this KRA. Add one above.</div>}
                            {(kra.kpis || []).map((kpi, kidx) => (
                              <div
                                key={kpi.id}
                                draggable
                                onDragStart={(e) => onDragStart(e, { sourceType: "kpi", sourceTpl: selectedTemplate.id, sourceKraId: kra.id, sourceKpiId: kpi.id })}
                                onDragOver={allowDrop}
                                onDrop={(e) => onDropOnKpiList(e, selectedTemplate.id, kra.id, kidx)}
                                className="border border-slate-100 rounded-lg p-3 bg-slate-50 hover:bg-slate-100 transition cursor-grab"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <input value={kpi.name} onChange={(e) => updateKPI(selectedTemplate.id, kra.id, kpi.id, { name: e.target.value })} className="px-2 py-1 w-full font-medium" placeholder="KPI Name" />
                                      <div className="w-20">
                                        <label className="block text-xs text-slate-500 text-center">Weight</label>
                                        <input type="number" value={kpi.weight || 0} onChange={(e) => updateKPI(selectedTemplate.id, kra.id, kpi.id, { weight: Number(e.target.value || 0) })} className="w-full px-2 py-1 border rounded text-center text-sm" />
                                      </div>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1 pl-2">Type: <span className="font-semibold">{kpi.type}</span> | Target: <span className="font-semibold">{kpi.target || '—'}</span></div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button onClick={() => removeKPI(selectedTemplate.id, kra.id, kpi.id)} className="px-2 py-1 text-rose-600 rounded hover:bg-rose-100 text-sm">Del</button>
                                  </div>
                                </div>
                                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 border-t pt-2 text-xs">
                                  <input placeholder="Target / Details" value={kpi.target || ""} onChange={(e) => updateKPI(selectedTemplate.id, kra.id, kpi.id, { target: e.target.value })} className="px-2 py-1 border rounded" />
                                  <input placeholder="Rating scale (e.g., 5)" value={kpi.ratingScale || ""} onChange={(e) => updateKPI(selectedTemplate.id, kra.id, kpi.id, { ratingScale: e.target.value })} className="px-2 py-1 border rounded" disabled={kpi.type !== 'rating'} />
                                  <input placeholder="Formula (e.g., 100 * VAR)" value={kpi.formula || ""} onChange={(e) => updateKPI(selectedTemplate.id, kra.id, kpi.id, { formula: e.target.value })} className="px-2 py-1 border rounded font-mono text-xs" disabled={kpi.type !== 'formula'} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Right: OKR Builder (1/3 width) */}
                  <div className="lg:col-span-1">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-semibold text-slate-800">Objectives & Key Results (OKRs)</h3>
                      <button onClick={() => addObjective(selectedTemplate.id)} className="px-3 py-1 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700 transition shadow-md" disabled={selectedTemplate.type === 'KRA'}>Add Objective</button>
                    </div>

                    <div className="space-y-4">
                      {(selectedTemplate.okrs || []).length === 0 && <div className="text-sm text-slate-500 p-8 text-center border-dashed border-2 border-slate-200 rounded-lg">No OKRs yet.</div>}
                      {(selectedTemplate.okrs || []).map((obj) => (
                        <div key={obj.id} className="border border-violet-200 rounded-xl p-3 bg-white shadow-sm">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 pr-3">
                              <input
                                value={obj.title}
                                onChange={(e) => updateObjective(selectedTemplate.id, obj.id, { title: e.target.value })}
                                className="w-full px-1 py-0.5 border-b text-lg font-semibold"
                                placeholder="Objective Title"
                              />
                              <div className="text-xs text-slate-500 mt-1">Key Results: {obj.keyResults.length}</div>
                            </div>
                            <button onClick={() => removeObjective(selectedTemplate.id, obj.id)} className="px-2 py-1 text-rose-600 rounded hover:bg-rose-50 text-sm">Del</button>
                          </div>

                          <div className="mt-3 space-y-2 pl-2">
                            <h4 className="text-sm font-medium text-violet-700">Key Results</h4>
                            {(obj.keyResults || []).map((kr) => (
                              <div key={kr.id} className="bg-violet-50 p-2 rounded-lg text-sm">
                                <div className="flex items-center gap-2">
                                  <input
                                    value={kr.name}
                                    onChange={(e) => updateKeyResult(selectedTemplate.id, obj.id, kr.id, { name: e.target.value })}
                                    className="w-full px-1 py-0.5"
                                    placeholder="KR Name"
                                  />
                                  <button onClick={() => removeKeyResult(selectedTemplate.id, obj.id, kr.id)} className="text-rose-600 text-xs hover:bg-rose-100 p-1 rounded">✕</button>
                                </div>
                                <input
                                  value={kr.target}
                                  onChange={(e) => updateKeyResult(selectedTemplate.id, obj.id, kr.id, { target: e.target.value })}
                                  className="w-full text-xs mt-1 px-1 py-0.5 border rounded"
                                  placeholder="Target (e.g., Increase NPS to 50)"
                                />
                              </div>
                            ))}
                            <button onClick={() => addKeyResult(selectedTemplate.id, obj.id)} className="text-violet-600 text-sm px-2 py-1 rounded-md border border-violet-200 hover:bg-violet-50 transition">+ Key Result</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Workflow Builder Section (Full Width) */}
                <div className="mt-6 p-4 border rounded-xl bg-slate-50">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xl font-semibold text-slate-800">Workflow & Approval Steps</h3>
                        <button onClick={() => addWorkflowStep(selectedTemplate.id)} className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition shadow-md">Add Step</button>
                    </div>

                    <div className="space-y-3">
                        {(selectedTemplate.workflow || []).length === 0 && <div className="text-sm text-slate-500 p-4 text-center border-dashed border-2 border-slate-200 rounded-lg">No workflow steps defined.</div>}
                        <ol className="flex flex-col md:flex-row gap-4">
                            {(selectedTemplate.workflow || []).map((step, index) => (
                                <li key={step.id} className="flex-1 p-4 border rounded-lg bg-white shadow-sm flex flex-col justify-between">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-md font-medium text-blue-700">Step {index + 1}</div>
                                        <button onClick={() => removeWorkflowStep(selectedTemplate.id, step.id)} className="text-rose-500 hover:text-rose-700 text-sm">✕</button>
                                    </div>
                                    <input
                                        value={step.name}
                                        onChange={(e) => updateWorkflowStep(selectedTemplate.id, step.id, { name: e.target.value })}
                                        className="w-full px-2 py-1 border rounded-md mb-2"
                                        placeholder="Step Name (e.g., Self-Assessment)"
                                    />
                                    <select
                                        value={step.role}
                                        onChange={(e) => updateWorkflowStep(selectedTemplate.id, step.id, { role: e.target.value })}
                                        className="w-full px-2 py-1 border rounded-md text-sm bg-white"
                                    >
                                        <option value="Employee">Employee</option>
                                        <option value="Manager">Manager</option>
                                        <option value="HR">HR</option>
                                        <option value="Reviewer">Reviewer</option>
                                    </select>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
              </>
            )}
          </section>
        </main>
        )}
      </div>

      {/* Main Template Editor Modal */}
      <Modal isOpen={isEditorOpen} onClose={closeEditor} title={`Editing Template: ${editorTemplate?.name || 'Loading...'}`} width="max-w-6xl">
        {editorTemplate && (
          <div className="space-y-6">
            <h3 className="text-lg font-bold border-b pb-2">Template General Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Template Name</label>
                <input
                  type="text"
                  value={editorTemplate.name}
                  onChange={(e) => setEditorTemplate({ ...editorTemplate, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Template Type</label>
                <select
                  value={editorTemplate.type}
                  onChange={(e) => setEditorTemplate({ ...editorTemplate, type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg mt-1 bg-white"
                >
                  <option value="KRA">KRA/KPI Focus</option>
                  <option value="OKR">OKR Focus</option>
                  <option value="Hybrid">Hybrid (KRA + OKR)</option>
                </select>
              </div>
            </div>
            
            <h3 className="text-lg font-bold border-b pb-2">Detailed KPI/Formula Editor</h3>
            <div className="space-y-4">
              {(editorTemplate.krAs || []).map((kra, kraIdx) => (
                <div key={kra.id} className="p-4 border rounded-xl bg-slate-100">
                  <h4 className="text-md font-semibold mb-3">{kra.title} ({kra.kpis.length} KPIs)</h4>
                  <div className="space-y-3">
                    {(kra.kpis || []).map((kpi) => (
                      <KPIEditorRow
                        key={kpi.id}
                        kpi={kpi}
                        onChange={(updatedKpi) => {
                          const updatedKras = editorTemplate.krAs.map((k) => 
                            k.id === kra.id ? { ...k, kpis: k.kpis.map((kp) => (kp.id === kpi.id ? updatedKpi : kp)) } : k
                          );
                          setEditorTemplate({ ...editorTemplate, krAs: updatedKras });
                        }}
                        onDelete={(kpiId) => {
                          const updatedKras = editorTemplate.krAs.map((k) => 
                            k.id === kra.id ? { ...k, kpis: k.kpis.filter((kp) => kp.id !== kpiId) } : k
                          );
                          setEditorTemplate({ ...editorTemplate, krAs: updatedKras });
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
              {(editorTemplate.krAs || []).length === 0 && <div className="text-center text-slate-500">Add KRAs on the main canvas to define KPIs here.</div>}
            </div>

            <h3 className="text-lg font-bold border-b pb-2">Cascading & Versions</h3>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Cascading Status</label>
                    <select
                        value={editorTemplate.cascading.enabled ? 'enabled' : 'disabled'}
                        onChange={(e) => setEditorTemplate({ ...editorTemplate, cascading: { enabled: e.target.value === 'enabled' } })}
                        className="w-full px-3 py-2 border rounded-lg mt-1 bg-white"
                    >
                        <option value="enabled">Enabled</option>
                        <option value="disabled">Disabled</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">Allows this template to cascade objectives to child templates.</p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Version History</label>
                    <div className="max-h-24 overflow-y-auto border p-2 rounded-lg bg-slate-50">
                        {(editorTemplate.versions || []).slice(-3).reverse().map((v, i) => (
                            <div key={i} className="text-xs text-slate-600">Snapshot at: {new Date(v.snapshotAt).toLocaleString()}</div>
                        ))}
                        {(editorTemplate.versions || []).length > 3 && <div className="text-xs text-slate-500">... ({editorTemplate.versions.length - 3} older versions hidden)</div>}
                        {(editorTemplate.versions || []).length === 0 && <div className="text-xs text-slate-500">No versions saved yet.</div>}
                    </div>
                </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={() => saveTemplate(editorTemplate)}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-semibold shadow-lg"
              >
                Save Template
              </button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
}